import { useEffect, useRef, useState } from "react";

/**
 * Public, unauthenticated brand-signup page served at /signup (no admin/tenant
 * chrome). Renders a Cloudflare Turnstile widget and posts to POST /api/signup.
 * Submit stays disabled until a Turnstile token is present.
 */

const TURNSTILE_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js";

type FieldErrors = Partial<Record<"name" | "email" | "brand_name" | "website" | "notes", string>>;

declare global {
  interface Window {
    turnstile?: {
      render: (
        el: HTMLElement,
        opts: {
          sitekey: string;
          callback: (token: string) => void;
          "expired-callback"?: () => void;
          "error-callback"?: () => void;
        }
      ) => string;
      reset: (id?: string) => void;
    };
  }
}

function useTurnstile(siteKey: string | undefined, onToken: (t: string) => void) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!siteKey) return;
    let cancelled = false;

    function render() {
      if (cancelled || !containerRef.current || !window.turnstile) return;
      if (widgetIdRef.current) return;
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey!,
        callback: (token: string) => onToken(token),
        "expired-callback": () => onToken(""),
        "error-callback": () => onToken(""),
      });
    }

    if (window.turnstile) {
      render();
    } else if (!document.querySelector(`script[src="${TURNSTILE_SRC}"]`)) {
      const s = document.createElement("script");
      s.src = TURNSTILE_SRC;
      s.async = true;
      s.defer = true;
      s.onload = render;
      document.head.appendChild(s);
    } else {
      const t = setInterval(() => {
        if (window.turnstile) {
          clearInterval(t);
          render();
        }
      }, 100);
      return () => clearInterval(t);
    }
    return () => {
      cancelled = true;
    };
  }, [siteKey, onToken]);

  const reset = () => {
    if (window.turnstile && widgetIdRef.current) window.turnstile.reset(widgetIdRef.current);
  };

  return { containerRef, reset };
}

export default function Signup() {
  const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
  const siteKey = env?.VITE_TURNSTILE_SITE_KEY;

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [brandName, setBrandName] = useState("");
  const [website, setWebsite] = useState("");
  const [wantsApi, setWantsApi] = useState(false);
  const [notes, setNotes] = useState("");
  const [token, setToken] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const { containerRef, reset } = useTurnstile(siteKey, setToken);

  // When no site key is configured (local dev), don't block submit on a token.
  const challengeSatisfied = !siteKey || token.length > 0;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError(null);
    setFieldErrors({});
    try {
      const res = await fetch("/api/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          email,
          brand_name: brandName,
          website,
          wants_api_access: wantsApi,
          notes,
          turnstile_token: token,
        }),
      });
      if (res.ok) {
        setSubmitted(true);
        return;
      }
      const body = (await res.json().catch(() => ({}))) as {
        error?: string;
        message?: string;
        fields?: FieldErrors;
      };
      if (body.fields) setFieldErrors(body.fields);
      setFormError(
        body.message ||
          (body.error === "turnstile_failed"
            ? "Challenge verification failed. Please try again."
            : body.error === "rate_limited"
              ? "Too many requests. Please try again later."
              : "Something went wrong. Please check the form and try again.")
      );
      reset();
      setToken("");
    } catch {
      setFormError("Network error. Please try again.");
      reset();
      setToken("");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 antialiased flex flex-col">
      <header className="border-b border-gray-200 bg-white">
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center gap-2">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-accent-500 text-white text-sm font-bold">
            S
          </span>
          <span className="text-base font-semibold text-gray-900">StoreSync</span>
        </div>
      </header>

      <main className="flex-1 w-full max-w-2xl mx-auto px-6 py-10">
        {submitted ? (
          <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-sm text-center">
            <h1 className="text-2xl font-semibold text-gray-900 mb-3">Thanks!</h1>
            <p className="text-sm text-gray-600">
              We'll review your request and email a sign-in link within 1 business day.
            </p>
          </div>
        ) : (
          <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-sm">
            <h1 className="text-2xl font-semibold text-gray-900 mb-1">Request StoreSync access</h1>
            <p className="text-sm text-gray-600 mb-6">
              Tell us about your brand and we'll get you set up.
            </p>

            <form className="space-y-5" onSubmit={submit}>
              <Field label="Your name" error={fieldErrors.name}>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  minLength={2}
                  maxLength={100}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </Field>

              <Field label="Work email" error={fieldErrors.email}>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </Field>

              <Field label="Brand / company name" error={fieldErrors.brand_name}>
                <input
                  type="text"
                  value={brandName}
                  onChange={(e) => setBrandName(e.target.value)}
                  required
                  maxLength={100}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </Field>

              <Field label="Company website" error={fieldErrors.website}>
                <input
                  type="text"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  required
                  placeholder="yourbrand.com"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </Field>

              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={wantsApi}
                  onChange={(e) => setWantsApi(e.target.checked)}
                  className="h-4 w-4"
                />
                I'd like API access
              </label>

              <Field label="Anything else? (optional)" error={fieldErrors.notes}>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  maxLength={1000}
                  rows={4}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </Field>

              {siteKey ? (
                <div ref={containerRef} className="cf-turnstile" />
              ) : (
                <p className="text-xs text-amber-600">
                  Turnstile site key not configured — challenge skipped in this environment.
                </p>
              )}

              {formError && (
                <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {formError}
                </div>
              )}

              <button
                type="submit"
                className="btn-primary w-full"
                disabled={submitting || !challengeSatisfied}
              >
                {submitting ? "Submitting…" : "Request access"}
              </button>
            </form>
          </div>
        )}
      </main>
    </div>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-gray-700 mb-1">{label}</span>
      {children}
      {error && <span className="mt-1 block text-xs text-red-600">{error}</span>}
    </label>
  );
}
